/**
 * Example of how an email display is typically displayed in WP3:
 *
 * <EmailContainer {...props}>
 *      <EmailContent>
 *           <EmailSubjectHeader subject={'cool email'} />
 *          <EmailDisplay>
 *               <EmailHeader {...props} />
 *              <EmailBody {...props} />
 *           </EmailContent>
 *       </EmailDisplay>
 * </EmailContainer>
 */

import React from 'react';
import EmailAttachments from "./EmailAttachments";
import EmailBody from "./EmailBody";
import EmailContainer from "./EmailContainer";
import EmailContent from "./EmailContent";
import EmailDisplay from "./EmailDisplay";
import EmailFilingProjectSelect from "./EmailFilingProjectSelect";
import EmailHeader from "./EmailHeader";
import EmailSubjectHeader from "./EmailSubjectHeader";
import { ErrorMessage, ErrorMessageWrapper } from "../messages";
import { Spinner } from "../loaders";
import { CenterPage } from "../utilities";
import { EmailViewPortProps } from "./types";
import EmailFilingButton from "./EmailFilingButton";

// TODO: attachments

export const EmailViewPort = ({
    allowFiling = false,
    commands,
    email,
    loadOptions: {
        error,
        isLoading
    },
    showProjectSelect = false
} : Readonly<EmailViewPortProps>) => {
    const {
        attachments,
        bccRecipients,
        body,
        ccRecipients,
        fromEmail,
        id,
        masterAccountId,
        projectId,
        sentDate,
        subject,
        toRecipients,
    } = email;

    const extraButtons = React.useMemo(() => {
        if (!allowFiling || !email || !commands.onFile || !commands.onUnFile) {
            return null;
        }
        return (
            <EmailFilingButton
                email={email}
                onFile={commands.onFile}
                onUnFile={commands.onUnFile}
            />
        );
    }, [ allowFiling, email ])

    if (isLoading) {
        return (
            <CenterPage>
                <Spinner />
            </CenterPage>
        );
    }

    if (error) {
        return (
            <ErrorMessageWrapper>
                <ErrorMessage error={error} />
            </ErrorMessageWrapper>
        );
    }

    return (
        <EmailContainer>
            <EmailContent>
                <EmailSubjectHeader subject={subject}>
                    {showProjectSelect && (
                        <div className="min-w-25">
                            <EmailFilingProjectSelect
                                mailId={id}
                                masterAccountId={masterAccountId ?? ""}
                                projectId={projectId}
                            />
                        </div>
                    )}
                </EmailSubjectHeader>
                <EmailDisplay>
                    <EmailHeader
                        bccRecipients={bccRecipients}
                        ccRecipients={ccRecipients}
                        commands={commands}
                        extraButtons={extraButtons}
                        fromEmail={fromEmail}
                        sentDate={sentDate}
                        toRecipients={toRecipients}
                    />
                    <EmailAttachments
                        attachments={attachments}
                        onOpenAttachment={commands.onOpenAttachment}
                    />
                    <EmailBody
                        html={body}
                    />
                </EmailDisplay>
            </EmailContent>
        </EmailContainer>
    );
};

export {
    EmailBody,
    EmailContainer,
    EmailContent,
    EmailDisplay,
    EmailHeader,
    EmailSubjectHeader
};
export default EmailViewPort;
